import { Fragment, useEffect, useRef, type ReactNode } from "react";
import { Settings } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useShellRightRailOccupied } from "../lib/shell-right-rail-context.js";
import { cn } from "../lib/utils.js";
import { Tooltip } from "../molecules/Tooltip/index.js";

/**
 * One entry in the settings sidebar. The library renders placement and
 * styling; the host decides what sections exist and what navigation means
 * (router, hash, state — not this component's business).
 */
export interface SettingsNavItem {
  readonly id: string;
  readonly label: string;
  /** One-line summary shown under the label. */
  readonly description?: string;
  readonly icon?: LucideIcon;
  /**
   * Optional heading this entry sits under. Consecutive entries sharing a group
   * render one heading; the host owns ordering, so a group that recurs after a
   * different one gets a second heading rather than a silent reorder.
   */
  readonly group?: string;
  /**
   * Id of the entry this one nests under. A child renders indented beneath its
   * parent, in the order the host supplies (the host owns ordering, as with
   * `group`); the active child carries `aria-current` like any other entry.
   * Used for a section with sub-views, e.g. KPI Manager → All KPIs / KPI
   * Library / Archived (PB-5134, d3).
   */
  readonly parentId?: string;
}

export interface SettingsLayoutProps {
  /** Sidebar entries — a registry the host supplies. */
  items: readonly SettingsNavItem[];
  /** Which entry is active. The host derives this from its router. */
  activeId: string;
  /** Fired when the user picks a section. The host navigates. */
  onNavigate: (id: string) => void;
  /** Heading block above the nav. Defaults suit the workspace settings area. */
  title?: string;
  subtitle?: string;
  /**
   * Accessible name for the sidebar `<nav>` landmark. Override when the
   * layout hosts something other than settings (e.g. the Admin console's
   * "Admin sections"), so assistive tech doesn't announce a mislabelled rail.
   */
  navLabel?: string;
  /**
   * Width of the content column. `default` keeps the reading-width cap that
   * suits forms and prose; `wide` releases it for data-dense areas.
   *
   * A settings form capped at reading width is right — a six-column log table
   * inside that cap is not: columns collapse to one word per line and the
   * rightmost is clipped. Every admin console that shows logs (Azure, AWS,
   * Atlassian) gives them the full window for the same reason. `relaxed` sits
   * between the two (max-w-5xl) for a form page with a meta aside beside its
   * main column (PB-5134 Settings pages). `console` is the lane the Admin
   * console AND the Settings pages share (PB-5178, widened by PB-5216): 90rem
   * — the wide-screen artboards' 1440px lane — so the content column grows
   * with the window until a 1704px window (264px rail plus the lane) pins it
   * there. A Settings form keeps its own reading width inside that lane
   * through `SettingsPageLayout mainWidth`; wide tables scroll inside the main
   * column instead of widening it.
   */
  contentWidth?: "default" | "wide" | "relaxed" | "console";
  /**
   * Rendered between the heading block and the items — the Settings rail's
   * section search lives here. Hidden below md, where the rail is icons only.
   */
  navHeaderSlot?: ReactNode;
  /**
   * Replaces the root's default height floor. The default suits a full page;
   * inside a dialog the panel owns the height, so `SettingsDialog` passes
   * `min-h-0` — without it the page floor forces the panel taller than the
   * viewport and the section body stops scrolling inside it.
   */
  className?: string;
  /** The active section's page content. */
  children: ReactNode;
  /**
   * Show the heading block above the nav. A surface whose own chrome already
   * names it — the settings DIALOG, whose panel header says the same words —
   * turns this off rather than printing the title twice.
   */
  showHeading?: boolean;
  /**
   * Pinned above the scrolling body, inside the content column. Lets a host put
   * its header beside the content rather than above the whole layout, so the
   * nav rail runs the full height. Rendered under a hairline, inside the SAME
   * width cap as the body (`LANE_CLASS`), so a tenant strip lines up with the
   * page header below it at every window width.
   */
  contentHeader?: ReactNode;
  /**
   * Whether the CONTENT COLUMN scrolls. Off for a section that manages its own
   * — a fixed header over a scrolling list. Sticky is not the same thing: a
   * sticky header still travels until it reaches the top, so the section it
   * belongs to appears to slide before it settles.
   */
  bodyScroll?: boolean;
}

/**
 * Settings-area chrome: a fixed-width section sidebar plus a scrollable,
 * width-capped content column. Data-blind — sections arrive as props,
 * navigation leaves as a callback. Ported from the wireframe settings shell
 * (PB-24 / ADR-0014 carry-rules: monorepo tokens, logical CSS).
 */
/** A group name as a single aria token, per OCCURRENCE.
 *
 * `aria-describedby` is a space-separated ID list, so a group called
 * "Data & privacy" would reference two ids that do not exist and silently
 * describe nothing. No current caller has a space; this makes that a property
 * of the code rather than of the caller list.
 *
 * Keyed on the group BOUNDARY, not on the name: this component deliberately
 * re-emits a heading when a group recurs after a different one, so the name
 * alone gave two headings the same DOM id and left every button in both
 * clusters pointing at an ambiguous reference. Counting per NAME fixed the
 * recurrence but not the other collision — normalising a label for the DOM is
 * many-to-one, so the distinct groups "Data & Privacy" and "Data-Privacy" both
 * reduce to the same token and both were the first of their name. A boundary
 * index cannot collide either way. The first boundary keeps the bare token.
 */
/** Width cap per lane. ONE map, read by the scrolling body AND by the pinned
 * `contentHeader` wrapper: a header rendered outside the cap sat flush against
 * the rail while the centred lane floated away from it on a wide window. */
const LANE_CLASS: Record<
  NonNullable<SettingsLayoutProps["contentWidth"]>,
  string
> = {
  default: "max-w-3xl",
  relaxed: "max-w-5xl",
  wide: "max-w-[110rem]",
  // 90rem (1440px, PB-5216): the Direction B artboards' wide-screen lane.
  // Reached from a 1704px window (264px rail + lane); fluid below it.
  console: "max-w-[90rem]",
};

function groupTokenId(group: string, boundary: number): string {
  const token = `settings-nav-group-${group.replace(/[^\w-]+/g, "-")}`;
  return boundary === 0 ? token : `${token}-${boundary + 1}`;
}

export function SettingsLayout({
  items,
  activeId,
  onNavigate,
  title = "Settings",
  subtitle = "Preferences",
  navLabel = "Settings sections",
  contentWidth = "default",
  showHeading = true,
  navHeaderSlot,
  contentHeader,
  bodyScroll = true,
  className,
  children,
}: SettingsLayoutProps) {
  // One scroll container serves every section, so switching sections used to
  // keep the previous one's `scrollTop` — you left General half way down and
  // arrived in Fields already scrolled past its search box, looking at rows
  // with no idea what was above them. A new section starts at the top.
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [activeId]);
  /* PB-5436: the Player AI side rail takes width from main without changing
     the window, so the icons-only shape also applies while it is open. The
     `max-md:` classes keep the small-window shape; `compact` adds the same
     shape at any width. Overlays reset the signal, so a SettingsDialog keeps
     its full nav. */
  const compact = useShellRightRailOccupied();

  return (
    <div
      className={cn("flex h-full", className ?? "min-h-[calc(100svh-96px)]")}
    >
      {/* Below md the rail collapses to icons only — desktop-first (DS
          decision #7) but still usable for phone reading — and so it does
          while the Player AI side rail is open (PB-5436). */}
      <nav
        className={cn(
          "flex w-66 shrink-0 flex-col border-e border-pb-border/80 bg-pb-shell py-4 max-md:w-14",
          compact && "w-14",
        )}
        aria-label={navLabel}
      >
        {showHeading ? (
          <div
            className={cn(
              "mb-2 flex items-center gap-2 border-b border-pb-border/70 px-4 pb-4 max-md:justify-center max-md:px-0",
              compact && "justify-center px-0",
            )}
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-md border border-pb-border/70 bg-pb-card-elevated">
              <Settings className="w-4 h-4 text-pb-text-muted" />
            </span>
            <div className={cn("min-w-0 max-md:hidden", compact && "hidden")}>
              <div className="text-sm font-semibold text-pb-text leading-tight">
                {title}
              </div>
              <div className="text-dense-1 text-pb-text-muted leading-tight">
                {subtitle}
              </div>
            </div>
          </div>
        ) : null}
        {navHeaderSlot ? (
          <div className={cn("mb-2 px-3 max-md:hidden", compact && "hidden")}>
            {navHeaderSlot}
          </div>
        ) : null}
        <div className="flex flex-col gap-0.5 px-2">
          {items.map((item, index) => {
            const Icon = item.icon;
            const isActive = item.id === activeId;
            const isChild = item.parentId !== undefined;
            /* Emit a heading when the group changes. Hidden below md, where the
               rail is icons only and a text heading would have nothing to head.
               NOT aria-hidden: the group names appear in no button label, so
               hiding them removed structure from assistive tech rather than
               de-duplicating it. Each button in the cluster points at it with
               `aria-describedby` — a described button, not a `role="group"`
               with an `aria-labelledby`, which would need a wrapper element
               this flat list does not have. */
            const previousGroup =
              index === 0 ? undefined : items[index - 1]?.group;
            const startsGroup =
              item.group !== undefined && item.group !== previousGroup;
            /* Which group BOUNDARY this item sits under, counted across the
               whole rail rather than per group name — see groupTokenId.
               Counted from the front rather than carried in a mutable counter,
               so `items.map` stays a pure function of the list. A nav rail is a
               handful of entries. */
            const boundariesBefore = items
              .slice(0, index)
              .filter(
                (previous, i) =>
                  previous.group !== undefined &&
                  previous.group !== items[i - 1]?.group,
              ).length;
            const groupBoundary = startsGroup
              ? boundariesBefore
              : boundariesBefore - 1;
            const button = (
              <button
                type="button"
                onClick={() => onNavigate(item.id)}
                aria-current={isActive ? "page" : undefined}
                aria-label={item.label}
                data-nav-level={isChild ? 2 : 1}
                data-parent-id={item.parentId}
                {...(item.group
                  ? {
                      "aria-describedby": groupTokenId(
                        item.group,
                        groupBoundary,
                      ),
                    }
                  : {})}
                className={cn(
                  "group relative flex items-start gap-2.5 rounded-md px-2.5 py-2 text-start transition-colors",
                  "max-md:justify-center",
                  compact && "justify-center",
                  // A child sits under its parent: indented, a touch tighter,
                  // and never wider than the rail on the icon-only floor.
                  isChild && "ms-6 py-1.5 max-md:ms-0",
                  isChild && compact && "ms-0",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pb-teal",
                  isActive
                    ? "bg-pb-cyan/10 text-pb-text"
                    : "text-pb-text-secondary hover:text-pb-text hover:pb-overlay-paint",
                )}
              >
                {isActive && (
                  <span
                    aria-hidden
                    className="absolute start-0 top-2 bottom-2 w-0.5 rounded-e bg-pb-cyan"
                  />
                )}
                {Icon ? (
                  <Icon
                    className={cn(
                      "w-4 h-4 mt-0.5 shrink-0",
                      isActive ? "text-pb-cyan-text" : "text-pb-text-muted",
                    )}
                  />
                ) : null}
                <span
                  className={cn("min-w-0 max-md:hidden", compact && "hidden")}
                >
                  <span className="block text-body font-medium leading-[18px] tracking-[-0.005em]">
                    {item.label}
                  </span>
                  {item.description ? (
                    <span className="block text-caption text-pb-text-muted leading-[15px] mt-px">
                      {item.description}
                    </span>
                  ) : null}
                </span>
              </button>
            );
            return (
              <Fragment key={item.id}>
                {startsGroup && item.group ? (
                  <span
                    id={groupTokenId(item.group, groupBoundary)}
                    role="presentation"
                    className={cn(
                      "px-2.5 pb-1 text-dense-1 font-semibold uppercase tracking-wide text-pb-text-muted max-md:hidden",
                      index === 0 ? "pt-1" : "pt-4",
                      compact && "hidden",
                    )}
                  >
                    {item.group}
                  </span>
                ) : null}
                {compact ? (
                  /* Icons only: a tooltip names each entry for sighted
                     users. describeChild is off because the button already
                     carries the label and its group description. */
                  <Tooltip
                    content={item.label}
                    placement="right"
                    describeChild={false}
                    wrapperClassName="flex flex-col"
                  >
                    {button}
                  </Tooltip>
                ) : (
                  button
                )}
              </Fragment>
            );
          })}
        </div>
      </nav>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {contentHeader ? (
          <div className="shrink-0 border-b border-pb-border/70">
            <div
              className={cn(
                "mx-auto px-8 max-md:px-4",
                LANE_CLASS[contentWidth],
              )}
            >
              {contentHeader}
            </div>
          </div>
        ) : null}
        <div
          ref={scrollRef}
          className={cn(
            "min-h-0 flex-1",
            bodyScroll ? "overflow-y-auto" : "flex flex-col overflow-hidden",
          )}
        >
          {/* `pt-5`, not `pt-8`: 32px of empty column above the section heading
              read as a gap rather than breathing room, especially beside a rail
              whose first item starts at the top. The bottom keeps its room so
              the last row never sits against the footer. */}
          <div
            className={cn(
              // The named container page bodies query instead of the window
              // (PB-5436); SettingsPageLayout's main column declares the same
              // name, and the nearest one wins.
              "@container/settings-page mx-auto px-8 pt-5 max-md:px-4 max-md:pt-4",
              LANE_CLASS[contentWidth],
              // Launcher reserve (PB-437): the Player AI launcher band floats over
              // Settings and Admin pages at every width, so the column keeps
              // block-end room for it until the shell owns that inset.
              bodyScroll ? "pb-24" : "flex min-h-0 w-full flex-1 flex-col",
            )}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
